import React from 'react';
import { Camera, Mail, KeyRound } from 'lucide-react';
import { barberRoleOptions } from '../../lib/barberRoles';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Full add / edit barber modal. State + submit are owned by the parent
// container (Barbers.js) so the create-vs-edit branching, invite flow,
// role PATCH and per-barber capability updates all live in one place.
// Split off from Barbers.js purely for readability.
export const BarberFormModal = ({
  // form
  formData, setFormData, editingBarber, handleAddBarber,
  onClose,
  // owner / self
  ownAsBarber, setOwnAsBarber, ownerName, ownerEmail, iAmABarber,
  // role gating
  callerRole, shopAdminUids, shop,
  // invite (add mode)
  giveLogin, setGiveLogin, loginEmail, setLoginEmail, inviteMsg,
  // avatar (edit mode)
  avatarV, avatarBusy, avatarErr, handleAvatarChange, handleAvatarClear,
  // login card (edit mode)
  authState, newLoginEmail, setNewLoginEmail,
  loginBusy, loginMsg, loginErr,
  handleChangeLoginEmail, handleSendReset, handleResendCreds,
}) => {
  // Owner detection uses shopAdminUids because `role` on barbers is
  // a display label ("Barber 1", "Barber 2"...) — not an auth role.
  const isOwnerRow = !!editingBarber && !!editingBarber.user_id
    && (shopAdminUids?.has?.(editingBarber.user_id) || false);
  // Role gating (Feb 2026): only shop_admin (owner) can change any
  // barber's role; the "Barber 1" slot is reserved for the owner and
  // locked wherever it appears.
  const canEditRoles = callerRole === 'shop_admin';
  const isEditingOwner = isOwnerRow;
  const roleOpts = barberRoleOptions(shop);

  // Escape to dismiss — the modal previously had no way out except
  // the Cancel button at the bottom, which was easy to miss if the
  // form scrolled off-screen. Test iteration_61 flagged this as a
  // trap door on tablet-portrait.
  React.useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="barber-form-modal" onClick={onClose}>
      <div className="barber-form-content" onClick={(e) => e.stopPropagation()} style={{ position: 'relative' }}>
        <button
          type="button"
          className="modal-close"
          aria-label="Close"
          onClick={onClose}
          data-testid="barber-form-close"
        >
          ×
        </button>
        <h3>{editingBarber ? `Edit ${editingBarber.name}` : 'Add Barber'}</h3>
        {/* Avatar upload — edit mode only (needs a saved barber id). */}
        {editingBarber && (
          <div
            className="field avatar-field"
            data-testid="barber-avatar-field"
            style={{
              display: 'flex',
              gap: 14,
              alignItems: 'center',
              background: 'var(--paper, #fafafa)',
              border: '1px solid var(--line)',
              padding: 12,
              borderRadius: 10,
              marginBottom: 12,
            }}
          >
            <div style={{ position: 'relative' }}>
              {editingBarber.avatar_url ? (
                <img
                  src={`${BACKEND_URL}/api/barbers/${editingBarber.id}/avatar?v=${avatarV}`}
                  alt={editingBarber.name}
                  data-testid="barber-avatar-preview"
                  style={{
                    width: 64, height: 64, borderRadius: '50%',
                    objectFit: 'cover', border: '1px solid var(--line)',
                    background: '#fff',
                  }}
                />
              ) : (
                <div
                  data-testid="barber-avatar-placeholder"
                  style={{
                    width: 64, height: 64, borderRadius: '50%',
                    background: 'var(--acc-soft, #e0e7ff)',
                    color: 'var(--acc, #4f46e5)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 700, fontSize: 24,
                    border: '1px solid var(--line)',
                  }}
                >
                  {(editingBarber.name || '?').charAt(0).toUpperCase()}
                </div>
              )}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>
                <Camera size={13} style={{ verticalAlign: '-2px', marginRight: 4 }} />
                Profile photo
              </div>
              <div style={{ fontSize: 11.5, color: 'var(--mute)', marginBottom: 8 }}>
                JPEG, PNG or WebP. Max 800&nbsp;KB. Shown on your public booking page.
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <label className="btn s" style={{ cursor: avatarBusy ? 'wait' : 'pointer' }}>
                  {avatarBusy ? 'Uploading…' : (editingBarber.avatar_url ? 'Replace' : 'Upload photo')}
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      e.target.value = '';
                      if (f) handleAvatarChange(f);
                    }}
                    disabled={avatarBusy}
                    style={{ display: 'none' }}
                    data-testid="barber-avatar-input"
                  />
                </label>
                {editingBarber.avatar_url && (
                  <button
                    type="button"
                    className="btn s"
                    onClick={handleAvatarClear}
                    disabled={avatarBusy}
                    data-testid="barber-avatar-clear"
                  >Remove</button>
                )}
              </div>
              {avatarErr && (
                <div className="modal-error" data-testid="barber-avatar-error" style={{ marginTop: 8 }}>{avatarErr}</div>
              )}
            </div>
          </div>
        )}
        <form onSubmit={handleAddBarber}>
          {!editingBarber && !iAmABarber && (
            <div className="field" style={{ background: 'var(--paper, #fafafa)', border: '1px solid var(--line)', padding: 12, borderRadius: 8 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', margin: 0 }}>
                <input
                  type="checkbox"
                  checked={ownAsBarber}
                  onChange={(e) => setOwnAsBarber(e.target.checked)}
                  data-testid="barber-add-self"
                />
                <span><b>Add me as a barber too</b> — {ownerName || ownerEmail}</span>
              </label>
            </div>
          )}
          <div className="field">
            <label>Name</label>
            <input
              className="inp"
              type="text"
              value={ownAsBarber ? (ownerName || 'Owner') : formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              disabled={ownAsBarber}
              placeholder="e.g. Marcus Bell"
              data-testid="barber-name"
            />
          </div>

          <div className="field">
            <label>Role
              {!canEditRoles && (
                <small style={{ color: 'var(--text-mute)', fontWeight: 'var(--fw-regular)', marginLeft: 6 }}>
                  — only the shop owner can change roles
                </small>
              )}
            </label>
            <select
              className="inp"
              value={isEditingOwner ? 'Barber 1' : (formData.role || '')}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              disabled={!canEditRoles || isEditingOwner}
              title={isEditingOwner ? 'The shop owner is always Barber 1' : undefined}
              data-testid="barber-role"
            >
              <option value="">No role</option>
              {roleOpts.map(o => (
                <option
                  key={o.id}
                  value={o.label}
                  disabled={o.disabled || (o.slot === 1 && !isEditingOwner)}
                >
                  {o.label}
                  {o.slot === 1 && !isEditingOwner ? ' — reserved for owner' : o.hint}
                </option>
              ))}
              {/* Preserve any legacy free-text role so it doesn't
                  silently collapse to "No role" the first time
                  the modal opens on an old barber row. Only
                  shown when the current value isn't already in
                  the canonical list. */}
              {!isEditingOwner && formData.role &&
                !roleOpts.some(o => o.label === formData.role) && (
                <option value={formData.role}>{formData.role} (legacy)</option>
              )}
            </select>
          </div>

          <div className="field">
            <label>
              Bio
              <small style={{ color: 'var(--mute)', fontWeight: 400, marginLeft: 6 }}>
                optional — shown on your public booking page next to this barber&rsquo;s photo
              </small>
            </label>
            <textarea
              className="inp"
              rows="3"
              value={formData.bio}
              onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
              placeholder="e.g. 12 years cutting hair, specialises in fades and beard sculpts."
              data-testid="barber-bio"
            />
          </div>

          <div className="field">
            <label>{editingBarber ? 'Notification email' : 'Email'} <small style={{ color: 'var(--mute)', fontWeight: 400 }}>optional — used for booking alerts</small></label>
            <input
              className="inp"
              type="email"
              value={ownAsBarber ? ownerEmail : formData.notification_email}
              onChange={(e) => setFormData({ ...formData, notification_email: e.target.value })}
              disabled={ownAsBarber}
              placeholder="barber@shop.co.uk"
              data-testid="barber-email"
            />
            {editingBarber && (
              <small style={{ color: 'var(--mute)', fontSize: 11, display: 'block', marginTop: 4 }}>
                This is where new-booking alerts go. To change the barber&rsquo;s login email, use the Login section below.
              </small>
            )}
          </div>

          <div className="field">
            <label>Phone <small style={{ color: 'var(--mute)', fontWeight: 400 }}>optional</small></label>
            <input
              className="inp"
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              placeholder="+447700900123"
              data-testid="barber-phone"
            />
          </div>

          {/* Single combined capability toggle. Sets both
              can_manage_bookings and can_take_payments to the
              same value — the two DB columns and the two
              backend guards remain independent so a shop can
              still split them later if we ever surface the
              finer control. Shop_admin only. */}
          {(callerRole === 'shop_admin' || callerRole === 'master_admin') && (
            <div className="field" style={{ borderTop: '1px dashed var(--line)', paddingTop: 14, marginTop: 6 }} data-testid="barber-perms">
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>Permissions</label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={!!formData.can_manage_bookings && !!formData.can_take_payments}
                  onChange={(e) => setFormData({
                    ...formData,
                    can_manage_bookings: e.target.checked,
                    can_take_payments:   e.target.checked,
                  })}
                  data-testid="barber-can-manage-and-take"
                />
                Can manage own bookings and record payments
                <small style={{ color: 'var(--mute)', fontWeight: 400, marginLeft: 4 }}>
                  — mark completed / no-show / cancel and record cash or card
                </small>
              </label>
            </div>
          )}

          {/* Owner-only "I also cut hair" toggle. Only shown when the
              row being edited is the shop_admin's own barber row. If
              flipped ON, the admin counts as a chair against the plan
              cap, shows up as a bookable column in the Diary, and is
              selectable in the customer booking wizard. Item 28 —
              lets the owner-who-cuts-hair stop paying for an extra
              seat they're double-counting, and the owner-who-doesn't
              stop showing up as a bookable barber. */}
          {editingBarber && isOwnerRow && (
            <div className="field" style={{ borderTop: '1px dashed var(--line)', paddingTop: 14, marginTop: 6 }} data-testid="barber-is-also-barber-row">
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={!!formData.is_also_barber}
                  onChange={(e) => setFormData({ ...formData, is_also_barber: e.target.checked })}
                  data-testid="barber-is-also-barber"
                  style={{ marginTop: 3 }}
                />
                <span>
                  <b>I also cut hair</b>
                  <small style={{ color: 'var(--mute)', display: 'block', fontWeight: 400, marginTop: 2, fontSize: 12 }}>
                    Adds you as a bookable chair in the diary and counts you toward your plan's barber seats. Leave off if you only manage the shop.
                  </small>
                </span>
              </label>
            </div>
          )}

          {!editingBarber && !ownAsBarber && (
            <div className="field" style={{ borderTop: '1px dashed var(--line)', paddingTop: 14, marginTop: 6 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={giveLogin}
                  onChange={(e) => setGiveLogin(e.target.checked)}
                  data-testid="barber-give-login"
                />
                Give this barber a login
              </label>
              {giveLogin && (
                <input
                  className="inp"
                  type="email"
                  placeholder="Barber's email address"
                  value={loginEmail}
                  onChange={(e) => setLoginEmail(e.target.value)}
                  style={{ marginTop: 10 }}
                  required
                  data-testid="barber-login-email"
                />
              )}
              {inviteMsg && (
                <div style={{ fontSize: 12, marginTop: 8, color: inviteMsg.includes('failed') ? 'var(--danger)' : 'var(--mute)' }}>
                  {inviteMsg}
                </div>
              )}
            </div>
          )}

          {/* Edit-mode login card — visible only when the barber
              has a linked auth account. Splits notification and
              login clearly so the two never drift silently. */}
          {editingBarber && authState?.has_login && (
            <div
              className="field"
              data-testid="barber-login-card"
              style={{
                borderTop: '1px dashed var(--line)',
                paddingTop: 14,
                marginTop: 6,
              }}
            >
              <div style={{ fontWeight: 600, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                <KeyRound size={14} /> Login &amp; password
              </div>
              <div style={{ fontSize: 12, color: 'var(--mute)', marginBottom: 10 }}>
                Current login email: <b data-testid="barber-current-login-email">{authState.email || '—'}</b>
                {authState.pending_email && authState.pending_email !== authState.email && (
                  <>
                    <br />
                    <span style={{ color: 'var(--amber, #92400e)' }} data-testid="barber-pending-email">
                      Pending verification: <b>{authState.pending_email}</b> — the barber must click the confirmation link in that inbox.
                    </span>
                  </>
                )}
              </div>

              <label style={{ fontSize: 12.5 }}>New login email</label>
              <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                <input
                  className="inp"
                  type="email"
                  value={newLoginEmail}
                  onChange={(e) => setNewLoginEmail(e.target.value)}
                  placeholder="new-address@shop.co.uk"
                  disabled={loginBusy}
                  data-testid="barber-new-login-email"
                  style={{ flex: 1 }}
                />
                <button
                  type="button"
                  className="btn s"
                  onClick={handleChangeLoginEmail}
                  disabled={loginBusy || !newLoginEmail.trim()}
                  data-testid="barber-change-login-email"
                >
                  <Mail size={13} /> {loginBusy ? 'Sending…' : 'Send verification'}
                </button>
              </div>
              <small style={{ fontSize: 11, color: 'var(--mute)', display: 'block', marginTop: 6 }}>
                The old login keeps working until the barber clicks the link at the new address, so a typo can&rsquo;t lock them out.
              </small>

              <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  className="btn s"
                  onClick={handleSendReset}
                  disabled={loginBusy}
                  data-testid="barber-send-reset"
                >
                  <KeyRound size={13} /> {loginBusy ? 'Sending…' : 'Send password reset link'}
                </button>
                <button
                  type="button"
                  className="btn s"
                  onClick={handleResendCreds}
                  disabled={loginBusy}
                  data-testid="barber-resend-creds"
                  title="Emails the barber a fresh temporary password. They'll be forced to change it on next sign-in."
                >
                  <Mail size={13} /> {loginBusy ? 'Sending…' : 'Resend credentials'}
                </button>
              </div>

              {loginMsg && (
                <div className="alert success" data-testid="barber-login-flash" style={{ marginTop: 10, fontSize: 12 }}>
                  {loginMsg}
                </div>
              )}
              {loginErr && (
                <div className="modal-error" data-testid="barber-login-error" style={{ marginTop: 10 }}>
                  {loginErr}
                </div>
              )}
            </div>
          )}

          <div className="form-actions">
            <button type="button" className="btn s" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn p" data-testid="barber-submit">
              {editingBarber ? 'Save changes' : 'Add Barber'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
